import { Injectable } from '@angular/core'
import { ApiClient, ApiClientFactory, serializeHttpParams } from '@ftr/api-shared'
import {
  CheckAnyPermissionQuery,
  CheckPermissionByEntityQuery,
  ConfigureRestrictedPermissionsBody,
} from '@ftr/contracts/api/court-system'
import { CheckPermissionQuery } from '@ftr/contracts/api/court-system/CheckPermissionQuery'
import {
  EntityType,
  GetRestrictedPermissionsQuery,
  ResourceType,
  RestrictedPermissionSet,
} from '@ftr/contracts/api/permissions'
import { UserGroupPermissionId } from '@ftr/contracts/api/user-group'
import { ErrorWithUserMessage } from '@ftr/contracts/shared/ErrorWithUserMessage'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, CombinedError, MapUtils, createAndAssign, mapData } from '@ftr/foundation'
import { CoreConfigurationService } from '@ftr/ui-court-system'

@Injectable()
export class PermissionService {
  private readonly courtSystemApiClient: ApiClient
  private readonly permissionsApiClient: ApiClient

  constructor(
    private apiClientFactory: ApiClientFactory,
    private configService: CoreConfigurationService,
  ) {
    this.courtSystemApiClient = this.apiClientFactory.build('/court-system')
    this.permissionsApiClient = this.apiClientFactory.build('/permissions')
  }

  configureRestrictedPermissions(courtSystemId: Uuid, body: ConfigureRestrictedPermissionsBody): ApiResult {
    return this.courtSystemApiClient.post({ path: `/${courtSystemId}/configureRestrictedPermissions`, body })
  }

  getEnabledRestrictedEntityTypes(courtSystemId: Uuid): ApiResult<EntityType[]> {
    return this.configService.getByCourtSystem(courtSystemId).pipe(
      mapData(config => {
        const enabledRestrictedPermissions: EntityType[] = []

        if (config.restrictedByLocation) {
          enabledRestrictedPermissions.push('location')
        }

        if (config.restrictedByDepartment && config.isDepartmentsEnabled) {
          enabledRestrictedPermissions.push('department')
        }

        return enabledRestrictedPermissions
      }),
    )
  }

  getEnabledRestrictedPermissionsByEntityType(
    courtSystemId: Uuid,
  ): ApiResult<Map<EntityType, Set<UserGroupPermissionId>>, CombinedError> {
    return ApiResult.combine([
      this.getRestrictedPermissions(courtSystemId),
      this.configService.getByCourtSystem(courtSystemId),
    ]).pipe(
      mapData(
        ([restrictedPermissions, config]) =>
          new Map([
            ['location', new Set(config.restrictedByLocation ? restrictedPermissions.location : [])],
            [
              'department',
              new Set(
                config.restrictedByDepartment && config.isDepartmentsEnabled ? restrictedPermissions.department : [],
              ),
            ],
          ]),
      ),
    )
  }

  getEnabledRestrictedPermissionsByPermission(
    courtSystemId: Uuid,
  ): ApiResult<Map<UserGroupPermissionId, Set<EntityType>>, CombinedError> {
    return this.getEnabledRestrictedPermissionsByEntityType(courtSystemId).pipe(
      mapData(byEntityType => MapUtils.invertSetMultimap(byEntityType)),
    )
  }

  getRestrictedPermissions(courtSystemId: Uuid): ApiResult<RestrictedPermissionSet> {
    return this.permissionsApiClient.get({
      path: '/restricted',
      params: serializeHttpParams(createAndAssign(GetRestrictedPermissionsQuery, { courtSystemId })),
    })
  }

  /**
   * A result of undefined means the user has the permission
   * @param courtSystemId The court system you are checking the resourceIds in
   * @param userGroupPermissionId The permission to check access to
   * @param resourceType The type of the resource id e.g. if its a recordingId, this is 'recording'
   * @param resourceIds the uuid to check access to
   */
  checkPermission(
    courtSystemId: Uuid,
    userGroupPermissionId: UserGroupPermissionId,
    resourceType?: ResourceType,
    resourceIds?: Uuid[],
  ): ApiResult<ErrorWithUserMessage | null> {
    return this.courtSystemApiClient.get({
      path: `${courtSystemId}/checkPermission`,
      params: serializeHttpParams(
        createAndAssign(CheckPermissionQuery, {
          userGroupPermissionId,
          resourceIds,
          resourceType,
        }),
      ),
    })
  }

  /**
   * A result of undefined means the user has the permission
   * @param courtSystemId The court system you are checking the resourceIds in
   * @param userGroupPermissionIds The permissions to check access to
   * @param resourceType The type of the resource id e.g. if its a recordingId, this is 'recording'
   * @param resourceIds the uuid to check access to
   */
  checkAnyPermission(
    courtSystemId: Uuid,
    userGroupPermissionIds: UserGroupPermissionId[],
    resourceType?: ResourceType,
    resourceIds?: Uuid[],
  ): ApiResult<ErrorWithUserMessage | null> {
    return this.courtSystemApiClient.get({
      path: `${courtSystemId}/checkAnyPermission`,
      params: serializeHttpParams(
        createAndAssign(CheckAnyPermissionQuery, {
          userGroupPermissionIds,
          resourceIds,
          resourceType,
        }),
      ),
    })
  }

  /**
   * @param courtSystemId The court system you are checking the resourceIds in
   * @param userGroupPermissionId The permission to check access to
   * @param resourceType The type of the resource id e.g. if its a recordingId, this is 'recording'
   * @param resourceIds the uuid to check access to
   */
  hasPermission(
    courtSystemId: Uuid,
    userGroupPermissionId: UserGroupPermissionId,
    resourceType?: ResourceType,
    resourceIds?: Uuid[],
  ): ApiResult<boolean> {
    return this.checkPermission(courtSystemId, userGroupPermissionId, resourceType, resourceIds).pipe(mapData(x => !x))
  }

  /**
   * @param courtSystemId The court system you are checking the resourceIds in
   * @param userGroupPermissionIds The permissions to check access to
   * @param resourceType The type of the resource id e.g. if its a recordingId, this is 'recording'
   * @param resourceIds the uuid to check access to
   */
  hasAnyPermission(
    courtSystemId: Uuid,
    userGroupPermissionIds: UserGroupPermissionId[],
    resourceType?: ResourceType,
    resourceIds?: Uuid[],
  ): ApiResult<boolean> {
    return this.checkAnyPermission(courtSystemId, userGroupPermissionIds, resourceType, resourceIds).pipe(
      mapData(x => !x),
    )
  }

  hasRecordingPermission(
    courtSystemId: Uuid,
    recordingId: Uuid,
    userGroupPermissionId: UserGroupPermissionId,
  ): ApiResult<boolean> {
    return this.hasPermission(courtSystemId, userGroupPermissionId, 'recording', [recordingId])
  }

  /**
   * A result of undefined means the user has the permission
   * @param courtSystemId The court system you are checking the resourceIds in
   * @param userGroupPermissionId The permission to check access to
   * @param entityType The type of the entity id e.g. if its a courtroomId, this is 'location'
   * @param entityId the uuid to check access to
   */
  checkPermissionByEntity(
    courtSystemId: Uuid,
    userGroupPermissionId: UserGroupPermissionId,
    entityType: EntityType,
    entityId: Uuid,
  ): ApiResult<ErrorWithUserMessage | null> {
    return this.courtSystemApiClient.get({
      path: `${courtSystemId}/checkPermissionByEntity`,
      params: serializeHttpParams(
        createAndAssign(CheckPermissionByEntityQuery, {
          userGroupPermissionId,
          entityType,
          entityId,
        }),
      ),
    })
  }

  /**
   * @param courtSystemId The court system you are checking the resourceIds in
   * @param userGroupPermissionId The permission to check access to
   * @param entityType The type of the entity id e.g. if its a courtroomId, this is 'location'
   * @param entityId the uuid to check access to
   */
  hasPermissionByEntity(
    courtSystemId: Uuid,
    userGroupPermissionId: UserGroupPermissionId,
    entityType: EntityType,
    entityId: Uuid,
  ): ApiResult<boolean> {
    return this.checkPermissionByEntity(courtSystemId, userGroupPermissionId, entityType, entityId).pipe(
      mapData(x => !x),
    )
  }
}
